// src/Components/Admin/InitialView.js

import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

// Firebase Imports
import { db } from '../../Firebase/firebase';
import { collection, onSnapshot, doc, deleteDoc, writeBatch } from 'firebase/firestore';

// Import Custom Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import {
  MenuAddNew,
  MenuAddNewGrey,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
  MenuTarget,
  MenuTargetGrey,
} from '../../assets/icons/CustomIcons';

// --- STYLING CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

// --- HELPERS: Formatters (For View Mode Only) ---
const formatLatLong = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const num = parseFloat(val);
  return isNaN(num) ? val : num.toFixed(6);
};

const formatZoom = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const num = parseFloat(val);
  return isNaN(num) ? val : num.toFixed(4);
};

// --- HELPER COMPONENT: Editable Cell (Buffered for typing without auto-sort) ---
const EditableCell = ({ value, onChange, autoFocus, placeholder, makeUppercase }) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');

  // Keep local value in sync with external value ONLY when we aren't actively focused on it
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const insertAtCursor = (textToInsert) => {
    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + textToInsert + val.slice(end);
    setLocalValue(newVal);
    onChange(newVal); // IMMEDIATELY UPDATE PARENT DIRTY STATE
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          start + textToInsert.length;
      }
    }, 0);
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    onChange(e.target.value); // IMMEDIATELY UPDATE PARENT DIRTY STATE
  };

  const handleKeyDown = (e) => {
    // Pressing Enter commits the edit by forcing a blur
    if (e.key === 'Enter') {
      inputRef.current.blur();
      return;
    }
    if (makeUppercase) {
      // Intercept single character keys to override casing
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const keyLower = e.key.toLowerCase();
        const keyUpper = e.key.toUpperCase();
        if (keyLower !== keyUpper) {
          e.preventDefault();
          const char = e.shiftKey ? keyLower : keyUpper;
          insertAtCursor(char);
        }
      }
    }
  };

  const handlePaste = (e) => {
    if (makeUppercase) {
      e.preventDefault();
      const paste = (e.clipboardData || window.clipboardData)
        .getData('text')
        .toUpperCase();
      insertAtCursor(paste);
    }
  };

  const handleBlur = () => {
    // Commit changes to the parent when the cell loses focus
    if (localValue !== (value || '')) {
      onChange(localValue);
    }
  };

  return (
    <TextField
      inputRef={inputRef}
      value={localValue}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onClick={(e) => e.stopPropagation()}
      variant='standard'
      size='small'
      fullWidth
      InputProps={{
        disableUnderline: true,
        style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
      }}
      sx={{
        backgroundColor: LIGHT_BLUE_BG,
        px: 1,
        borderRadius: 1,
        border: `1px solid ${ORANGE_COLOR}`,
        boxSizing: 'border-box',
        '& .MuiInputBase-root': {
          height: '100%',
          fontSize: '0.8rem',
          color: DARK_BLUE,
        },
        '& .MuiInputBase-input': {
          color: DARK_BLUE,
          fontWeight: 'normal',
        },
        '&:hover': { backgroundColor: LIGHT_BLUE_BG },
        '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
      }}
    />
  );
};

export default function InitialView({ getMapCurrentState, setIsDirty }) {
  // --- STATE ---
  const [activeAction, setActiveAction] = useState('VIEW');
  const [selectedViewId, setSelectedViewId] = useState(null);

  // Data State
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Global Settings State
  const [makeUppercase, setMakeUppercase] = useState(true);

  // Usage State (Count of users using a specific view)
  const [usageCounts, setUsageCounts] = useState({});

  // --- EDIT STATE ---
  const [edits, setEdits] = useState({});
  const [tempNewItem, setTempNewItem] = useState(null);
  const [rawCapture, setRawCapture] = useState(null);

  // --- SCROLL REF ---
  const tableContainerRef = useRef(null);

  // --- ALERT STATE ---
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [showDefaultDeleteAlert, setShowDefaultDeleteAlert] = useState(false);
  const [showArchiveDefaultAlert, setShowArchiveDefaultAlert] = useState(false);
  const [showUsageAlert, setShowUsageAlert] = useState(false);
  const [usageAlertMessage, setUsageAlertMessage] = useState('');

  // --- FIRESTORE SUBSCRIPTION 0: Text Formatting Settings ---
  useEffect(() => {
    const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMakeUppercase(data.makeUppercase !== undefined ? !!data.makeUppercase : true);
      }
    });
    return () => unsubFmt();
  }, []);

  // --- FIRESTORE SUBSCRIPTION 1: Initial Views ---
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'initialViews'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setViews(fetchedData);
        setLoading(false);

        if (selectedViewId && !fetchedData.find((v) => v.id === selectedViewId)) {
          if (tempNewItem && selectedViewId === tempNewItem.id) {
            return;
          }
          setSelectedViewId(null);
        }
      },
      (error) => {
        console.error('Error fetching initial views:', error);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [selectedViewId, tempNewItem]);

  // --- FIRESTORE SUBSCRIPTION 2: Permitted Users ---
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'permittedUsers'),
      (snapshot) => {
        const counts = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.initialViewId) {
            const viewId = String(data.initialViewId);
            counts[viewId] = (counts[viewId] || 0) + 1;
          }
        });
        setUsageCounts(counts);
      },
      (error) => {
        console.error('Error fetching usage counts:', error);
      },
    );
    return () => unsubscribe();
  }, []);

  // --- SCROLL EFFECT ---
  useEffect(() => {
    if (activeAction === 'ADD_NEW' && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [activeAction, tempNewItem]);

  const checkIsDirty = () => {
    if (tempNewItem) return true;
    if (Object.keys(edits).length > 0) return true;
    return false;
  };

  const isDirty = checkIsDirty();
  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(isDirty);
    }
  }, [isDirty, setIsDirty]);

  const resetInterface = () => {
    setActiveAction('VIEW');
    setEdits({});
    setTempNewItem(null);
    setRawCapture(null);
    setShowUnsavedAlert(false);
    setSelectedViewId(null);
  };

  const handleFieldChange = (id, field, val) => {
    if (tempNewItem && tempNewItem.id === id) {
      setTempNewItem((prev) => ({ ...prev, [field]: val }));
    } else {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [field]: val },
      }));
    }
  };

  const handleSetDefault = (targetId) => {
    if (tempNewItem && tempNewItem.id === targetId) {
      setTempNewItem((prev) => ({ ...prev, isDefault: true }));
    } else if (tempNewItem && tempNewItem.isDefault) {
      setTempNewItem((prev) => ({ ...prev, isDefault: false }));
    }

    views.forEach((v) => {
      if (v.id === targetId) {
        if (!v.isDefault) {
          handleFieldChange(v.id, 'isDefault', true);
        }
      } else {
        const currentIsDefault =
          edits[v.id]?.isDefault !== undefined ? edits[v.id].isDefault : v.isDefault;

        if (currentIsDefault) {
          handleFieldChange(v.id, 'isDefault', false);
        }
      }
    });
  };

  const handleSetArchived = (id, currentArchived, currentDefault) => {
    if (!currentArchived && currentDefault) {
      setShowArchiveDefaultAlert(true);
      return;
    }
    handleFieldChange(id, 'isArchived', !currentArchived);
  };

  const handleTargetCapture = () => {
    if (!selectedViewId || !getMapCurrentState) return;

    const mapData = getMapCurrentState();
    if (mapData) {
      // console.log('📍 RAW Google Maps Data:', mapData);
      setRawCapture(mapData);

      const targetId = selectedViewId;
      const updates = {
        lat: formatLatLong(mapData.lat),
        lng: formatLatLong(mapData.lng),
        zoom: formatZoom(mapData.zoom),
      };

      if (tempNewItem && tempNewItem.id === targetId) {
        setTempNewItem((prev) => ({ ...prev, ...updates }));
      } else {
        setEdits((prev) => ({
          ...prev,
          [targetId]: { ...(prev[targetId] || {}), ...updates },
        }));
      }
    }
  };

  const handleHeaderAction = async (action) => {
    if (action === 'ADD_NEW') {
      const newId = `new_${Date.now()}`;
      const newRow = {
        id: newId,
        name: '',
        lat: '',
        lng: '',
        zoom: '',
        isDefault: false,
        isArchived: false,
      };
      setTempNewItem(newRow);
      setSelectedViewId(newId);
      setActiveAction('ADD_NEW');
    } else if (action === 'TARGET') {
      handleTargetCapture();
    } else if (action === 'SAVE') {
      try {
        const batch = writeBatch(db);

        if (tempNewItem) {
          const finalLat =
            rawCapture && selectedViewId === tempNewItem.id
              ? rawCapture.lat
              : parseFloat(tempNewItem.lat);
          const finalLng =
            rawCapture && selectedViewId === tempNewItem.id
              ? rawCapture.lng
              : parseFloat(tempNewItem.lng);
          const finalZoom =
            rawCapture && selectedViewId === tempNewItem.id
              ? rawCapture.zoom
              : parseFloat(tempNewItem.zoom);

          const docRef = doc(collection(db, 'initialViews'));
          const payload = {
            ...tempNewItem,
            lat: finalLat || 0,
            lng: finalLng || 0,
            zoom: finalZoom || 0,
          };
          delete payload.id;
          batch.set(docRef, payload);
        }

        Object.keys(edits).forEach((id) => {
          const docRef = doc(db, 'initialViews', id);
          const changes = { ...edits[id] };

          if (rawCapture && selectedViewId === id) {
            if (changes.lat !== undefined) changes.lat = rawCapture.lat;
            if (changes.lng !== undefined) changes.lng = rawCapture.lng;
            if (changes.zoom !== undefined) changes.zoom = rawCapture.zoom;
          } else {
            if (changes.lat) changes.lat = parseFloat(changes.lat);
            if (changes.lng) changes.lng = parseFloat(changes.lng);
            if (changes.zoom) changes.zoom = parseFloat(changes.zoom);
          }

          batch.update(docRef, changes);
        });

        await batch.commit();
        // console.log('Batch Save Complete');
        resetInterface();
      } catch (error) {
        console.error('Error saving views:', error);
      }
    } else if (action === 'DELETE') {
      if (!selectedViewId) return;

      const viewToDelete =
        views.find((v) => v.id === selectedViewId) ||
        (tempNewItem && tempNewItem.id === selectedViewId ? tempNewItem : null);

      if (!viewToDelete) return;

      if (viewToDelete.isDefault) {
        setShowDefaultDeleteAlert(true);
        return;
      }

      if (selectedViewId !== tempNewItem?.id) {
        const usageCount = usageCounts[selectedViewId] || 0;
        if (usageCount > 0) {
          setUsageAlertMessage(
            `You cannot delete this Initial View because it is currently used by ${usageCount} markers. Archive it instead`,
          );
          setShowUsageAlert(true);
          return;
        }
      }
      setShowDeleteAlert(true);
    } else if (action === 'CLOSE') {
      if (checkIsDirty()) {
        setShowUnsavedAlert(true);
      } else {
        resetInterface();
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (tempNewItem && selectedViewId === tempNewItem.id) {
      setTempNewItem(null);
      setSelectedViewId(null);
    } else if (selectedViewId) {
      try {
        await deleteDoc(doc(db, 'initialViews', String(selectedViewId)));
        setSelectedViewId(null);
      } catch (error) {
        console.error('Error deleting view:', error);
      }
    }
    setShowDeleteAlert(false);
    resetInterface();
  };

  const handleRowClick = (id) => {
    setSelectedViewId(id === selectedViewId ? null : id);
    setRawCapture(null);
  };

  const isAddMode = activeAction === 'ADD_NEW';
  const canAdd = true;
  const canTarget = selectedViewId !== null;
  const canSave = isDirty;
  const canDelete = selectedViewId !== null;
  const canClose = isAddMode || isDirty;

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  };

  const displayViews = tempNewItem ? [...views, tempNewItem] : views;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          px: '5px',
          mb: 2,
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canAdd && handleHeaderAction('ADD_NEW')}
            disabled={!canAdd}>
            {canAdd ? (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            disabled={!canTarget}
            onClick={() => handleHeaderAction('TARGET')}
            sx={{
              p: 0,
              transform: 'translateY(-5px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.9,
              justifyContent: 'center',
            }}>
            {canTarget ? (
              <MenuTarget sx={{ fontSize: 31, mt: 1 }} />
            ) : (
              <MenuTargetGrey sx={{ fontSize: 31, mt: 1 }} />
            )}
            <Typography
              variant='caption'
              sx={{
                fontSize: '10px',
                lineHeight: 0,
                color: canTarget ? 'black' : '#9e9e9e',
              }}>
              Mark Centre
            </Typography>
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canSave && handleHeaderAction('SAVE')}
            disabled={!canSave}>
            {canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canDelete && handleHeaderAction('DELETE')}
            disabled={!canDelete}>
            {canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => canClose && handleHeaderAction('CLOSE')}
            disabled={!canClose}>
            {canClose ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '2px solid #2196f3',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
          // AMENDED: Removed scrollbar hiding CSS
        }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table stickyHeader size='small' sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '36px', // AMENDED: Compact height
                  '& th': {
                    bgcolor: '#e3f2fd',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #2196f3',
                    py: '4px', // AMENDED: Compressed padding
                  },
                }}>
                <TableCell padding='checkbox' sx={{ width: '50px' }} />
                <TableCell sx={{ width: '25%' }}>Initial View Name</TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Default
                </TableCell>
                <TableCell sx={{ width: '20%' }}>Centre Lat</TableCell>
                <TableCell sx={{ width: '20%' }}>Centre Long</TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Zoom
                </TableCell>
                <TableCell align='center' sx={{ width: '10%' }}>
                  Archived
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayViews.map((row) => {
                const isSelected = selectedViewId === row.id;
                const currentData = { ...row, ...(edits[row.id] || {}) };
                if (tempNewItem && row.id === tempNewItem.id) {
                  Object.assign(currentData, tempNewItem);
                }

                const displayLat = formatLatLong(currentData.lat);
                const displayLng = formatLatLong(currentData.lng);
                const displayZoom = formatZoom(currentData.zoom);

                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleRowClick(row.id)}
                    selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      opacity: 1,
                      height: '32px', // AMENDED: Compact row height
                      '& td': { py: '4px' }, // AMENDED: Compressed cell padding
                      '&.Mui-selected': {
                        backgroundColor: 'transparent !important',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        onChange={() => handleRowClick(row.id)}
                        sx={{ p: 0.5 }} // AMENDED: Reduced checkbox margin
                      />
                    </TableCell>

                    <TableCell>
                      <EditableCell
                        value={currentData.name}
                        placeholder='Enter View Name'
                        makeUppercase={makeUppercase}
                        autoFocus={tempNewItem && row.id === tempNewItem.id}
                        onChange={(val) => handleFieldChange(row.id, 'name', val)}
                      />
                    </TableCell>

                    <TableCell align='center'>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          size='small'
                          checked={!!currentData.isDefault}
                          onChange={() => handleSetDefault(row.id)}
                          sx={{ p: 0.5 }} // AMENDED: Reduced checkbox margin
                        />
                      </Box>
                    </TableCell>

                    <TableCell>
                      <EditableCell
                        value={displayLat}
                        onChange={(val) => handleFieldChange(row.id, 'lat', val)}
                      />
                    </TableCell>

                    <TableCell>
                      <EditableCell
                        value={displayLng}
                        onChange={(val) => handleFieldChange(row.id, 'lng', val)}
                      />
                    </TableCell>

                    <TableCell align='center'>
                      <EditableCell
                        value={displayZoom}
                        onChange={(val) => handleFieldChange(row.id, 'zoom', val)}
                      />
                    </TableCell>

                    <TableCell align='center'>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          size='small'
                          checked={!!currentData.isArchived}
                          onChange={() =>
                            handleSetArchived(
                              row.id,
                              !!currentData.isArchived,
                              !!currentData.isDefault,
                            )
                          }
                          sx={{ p: 0.5 }} // AMENDED: Reduced checkbox margin
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* AMENDED: Updated Z-Index from 1605 to 100005 */}
      <ConfirmDialog
        open={showUnsavedAlert}
        onClose={() => setShowUnsavedAlert(false)}
        onConfirm={resetInterface}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ConfirmDialog
        open={showDeleteAlert}
        onClose={() => setShowDeleteAlert(false)}
        onConfirm={handleConfirmDelete}
        message='This action will permanently delete this information. Are you sure you want to proceed?'
        severity='error'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ConfirmDialog
        open={showDefaultDeleteAlert}
        onClose={() => setShowDefaultDeleteAlert(false)}
        message='You cannot delete the default initial view'
        severity='warning'
        autoClose={2000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ConfirmDialog
        open={showArchiveDefaultAlert}
        onClose={() => setShowArchiveDefaultAlert(false)}
        message='You cannot archive the Default view'
        severity='warning'
        autoClose={2000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />

      <ConfirmDialog
        open={showUsageAlert}
        onClose={() => setShowUsageAlert(false)}
        message={usageAlertMessage}
        severity='warning'
        autoClose={2000}
        disableRestoreFocus={true}
        cancelText={null}
        sx={{ zIndex: 100005 }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
      />
    </Box>
  );
}
